/**
 * Utility functions for app-like features
 */

// Check if the device supports vibration
export const supportsVibration = () => {
  return 'vibrate' in navigator;
};

// Trigger haptic feedback
export const vibrate = (pattern = 50) => {
  if (supportsVibration()) {
    navigator.vibrate(pattern);
  }
};

// Check if the app is in standalone mode (PWA installed)
export const isInStandaloneMode = () => {
  return (window.matchMedia('(display-mode: standalone)').matches) || 
         (window.navigator.standalone) || 
         document.referrer.includes('android-app://');
};

// Check if the device is a mobile device
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Check if the app is in fullscreen mode
export const isInFullscreenMode = () => {
  return !!document.fullscreenElement;
};

// Request fullscreen mode
export const requestFullscreen = () => {
  const docEl = document.documentElement;
  
  if (docEl.requestFullscreen) {
    return docEl.requestFullscreen();
  } else if (docEl.webkitRequestFullscreen) { /* Safari */
    return docEl.webkitRequestFullscreen();
  } else if (docEl.msRequestFullscreen) { /* IE11 */
    return docEl.msRequestFullscreen();
  }
  
  return Promise.reject('Fullscreen API not supported');
};

// Exit fullscreen mode
export const exitFullscreen = () => {
  if (document.exitFullscreen) {
    return document.exitFullscreen();
  } else if (document.webkitExitFullscreen) { /* Safari */
    return document.webkitExitFullscreen();
  } else if (document.msExitFullscreen) { /* IE11 */
    return document.msExitFullscreen();
  }
  
  return Promise.reject('Fullscreen API not supported');
};

// Toggle fullscreen mode
export const toggleFullscreen = async () => {
  try {
    if (isInFullscreenMode()) {
      await exitFullscreen();
    } else {
      await requestFullscreen();
    }
    // Provide haptic feedback
    vibrate(100);
    return true;
  } catch (error) {
    console.error('Error toggling fullscreen:', error);
    return false;
  }
};

// Set theme color meta tag for status bar color
export const setStatusBarColor = (color) => {
  let meta = document.querySelector('meta[name="theme-color"]');
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  
  meta.content = color;
};

// Apply app-like optimizations
export const applyAppOptimizations = () => {
  // Prevent pull-to-refresh on mobile
  document.body.style.overscrollBehavior = 'none';
  
  // Prevent text selection for app-like feel
  document.body.style.userSelect = 'none';
  
  // Set viewport to prevent scaling
  const viewportMeta = document.querySelector('meta[name="viewport"]');
  if (viewportMeta) {
    viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
  }
  
  // Set status bar color to match theme
  const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
  setStatusBarColor(isDarkMode ? '#121212' : '#ffffff');
  
  // Add event listener for dark mode changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    setStatusBarColor(e.matches ? '#121212' : '#ffffff');
  });
};

// Initialize app mode features
export const initAppMode = () => {
  applyAppOptimizations();
  
  // Add class to body for app-specific styles
  document.body.classList.add('app-mode');
  
  // Listen for fullscreen changes
  document.addEventListener('fullscreenchange', () => {
    if (isInFullscreenMode()) {
      document.body.classList.add('fullscreen-mode');
    } else {
      document.body.classList.remove('fullscreen-mode');
    }
  });
  
  // Return whether device is mobile
  return isMobileDevice();
};